import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';

const HEALTHCOM_API_NAME = 'healthcom-api';
const HEALTHCOM_API_NOAUTH_NAME = 'healthcom-api-noauth';

const healthcomService = () => {
  /**
   * Fetch the authenticated patient's data from the HealthCom API.
   * This returns patient info, conditions, exercises, advice, and clinic details.
   * When a phone/email maps to multiple clinics, the response includes a `clinics` array.
   * When a clinic has multiple patients sharing the credential, `patients` is populated.
   */
  const me = async (): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, '/me', {});
  };

  /**
   * Discover which clinics the authenticated user belongs to.
   * Returns { clinics: PatientClinic[] } with path (vanity slug) for each.
   */
  const meClinics = async (): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, '/me/clinics', {});
  };

  /**
   * Fetch portal data (exercises, conditions, advice) for the authenticated user
   * at a specific clinic identified by its vanity path.
   * Returns { patients: PatientInfo[] }.
   */
  const mePortal = async (path: string): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/${path}/portal`, {});
  };

  /**
   * Fetch portal data by clinicId instead of path. Used after the user has
   * selected a clinic in the authed portal — clinics without a vanity URL
   * path are still reachable this way.
   * Returns { patients: PatientInfo[] }.
   */
  const mePortalByClinicId = async (clinicId: number): Promise<any> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/clinic/${clinicId}/portal`, {});
  };

  /**
   * Initiate portal login — ensures the Cognito user exists for the given email/phone.
   * Called before signIn() to create the user if they don't exist yet.
   */
  const loginPortal = async ({
    email,
    phone,
  }: {
    email?: string;
    phone?: string;
  }): Promise<any> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NOAUTH_NAME, '/login/portal', {
      body: { email, phone },
    });
  };

  /**
   * Submit a pain level survey response after completing exercises.
   * `patientId` is required — one Cognito login can have several
   * patient records (parent + kids), so the backend won't try to
   * resolve a single owner.
   */
  const painLevelSurvey = async ({
    level,
    patientId,
  }: {
    level: number;
    patientId: string;
  }): Promise<any> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, '/pain-level-survey', {
      body: { level, patientId },
    });
  };

  /**
   * Pay down the patient's outstanding balance using a saved
   * USAePay card on file. The backend allocates the amount across
   * the patient's open invoices oldest-first; the patient doesn't
   * pick which transactions to apply against. Pass `payMethodKey`
   * to charge a specific saved card; omit it to default to the
   * first one on the customer record.
   */
  const payBalance = async ({
    patientId,
    amount,
    payMethodKey,
  }: {
    patientId: string;
    amount: number;
    payMethodKey?: string;
  }): Promise<{
    message?: string;
    amount?: number;
    invoicesPaid?: number;
    data?: { billingKey?: string; refnum?: string };
  }> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, `/me/${patientId}/pay-balance`, {
      body: { amount, payMethodKey },
    });
  };

  /**
   * List the patient's saved payment methods (USAePay + Dejavoo)
   * so the Balance page can render a card picker. Each card is
   * tagged with its processor and a `chargeable` flag — only
   * `chargeable: true` cards can be charged from the portal today.
   * Also returns `addCard` config (processor + ecommToken) when the
   * clinic supports adding cards from the portal.
   */
  const listPaymentMethods = async (
    patientId: string
  ): Promise<{
    data: {
      key: string;
      processor: 'usaepay' | 'dejavoo';
      cardholder?: string;
      cardType?: string;
      lastFour?: string;
      isDefault?: boolean;
      chargeable: boolean;
    }[];
    addCard?: { processor: 'dejavoo'; ecommToken: string } | null;
  }> => {
    return ChiroUpAPI.get(HEALTHCOM_API_NAME, `/me/${patientId}/payment-methods`, {});
  };

  /**
   * Save a new payment method for the patient. Today only Dejavoo
   * via the iPOSpays Freedom-to-Design SDK is supported — the
   * frontend tokenizes the card client-side and sends the
   * `paymentTokenId` here.
   */
  const addPaymentMethod = async ({
    patientId,
    paymentTokenId,
    cardholderName,
    postalCode,
    cardLastFour,
    cardType,
  }: {
    patientId: string;
    paymentTokenId: string;
    cardholderName?: string;
    postalCode?: string;
    cardLastFour?: string;
    cardType?: string;
  }): Promise<{
    message?: string;
    data?: {
      key: string;
      processor: 'dejavoo';
      cardLastFour?: string | null;
      cardType?: string | null;
      cardholder?: string | null;
    };
  }> => {
    return ChiroUpAPI.post(HEALTHCOM_API_NAME, `/me/${patientId}/payment-methods`, {
      body: {
        paymentTokenId,
        cardholderName,
        postalCode,
        cardLastFour,
        cardType,
      },
    });
  };

  /**
   * Update email subscription preferences.
   */
  const surveyEmails = async (unsubscribe: 0 | 1): Promise<any> => {
    return ChiroUpAPI.put(HEALTHCOM_API_NAME, '/survey-emails', {
      body: { unsubscribe },
    });
  };

  return {
    me,
    meClinics,
    mePortal,
    mePortalByClinicId,
    loginPortal,
    painLevelSurvey,
    payBalance,
    listPaymentMethods,
    addPaymentMethod,
    surveyEmails,
  };
};

export default healthcomService();

export { HEALTHCOM_API_NAME, HEALTHCOM_API_NOAUTH_NAME };
